import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  Spinner,
  Center,
  HStack,
  Icon,
  Tooltip,
  Flex,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { campaignService } from "../../../../services/campaignService";
import { MdInfo } from "react-icons/md";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";

export default function CampaignVisualizations() {
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(null);
  
  const textColor = useColorModeValue("secondaryGray.900", "white");
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await campaignService.getCampaignPerformance();
        
        if (response.success) {
          setPerformanceData(response.data);
        }
      } catch (error) {
        console.error('Kampanya performans verileri getirme hatası:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  if (loading) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Kampanya Trend Analizi
        </Text>
        <Center py={8}>
          <Spinner color="brand.500" />
        </Center>
      </Card>
    );
  }
  
  if (!performanceData || !performanceData.campaigns || performanceData.campaigns.length === 0) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Kampanya Trend Analizi
        </Text>
        <Center py={8}>
          <Text color="gray.500">Henüz kampanya verisi bulunmuyor</Text>
        </Center>
      </Card>
    );
  }
  
  // Zaman bazlı veri (son 30 gün - tüm kampanyaların toplamı)
  const dailyData = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    
    let totalUsage = 0;
    let totalRevenue = 0;
    
    performanceData.campaigns.forEach(campaign => {
      if (campaign.dailyUsage && campaign.dailyUsage[dateStr]) {
        totalUsage += campaign.dailyUsage[dateStr];
        // Bu gün için gelir tahmini (ortalama sipariş tutarı kullanarak)
        const avgOrderValue = campaign.usageCount > 0 
          ? campaign.totalRevenue / campaign.usageCount 
          : 0;
        totalRevenue += campaign.dailyUsage[dateStr] * avgOrderValue;
      }
    });
    
    dailyData.push({
      date: dayjs(dateStr).format('DD/MM'),
      usage: totalUsage,
      revenue: Math.round(totalRevenue * 100) / 100
    });
  }
  
  return (
    <Card h="100%">
      <Flex direction="column" h="100%" justify="space-between">
        <HStack spacing={2}>
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Kampanya Trend Analizi
          </Text>
          <Tooltip 
            label="Kampanya kullanım ve gelir trendlerini görsel olarak analiz edin" 
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            openDelay={300}
          >
            <Box display="inline-block">
              <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
            </Box>
          </Tooltip>
        </HStack>
        
        {/* Line Chart - Zaman bazlı trend */}
        <Box flex="1" display="flex" alignItems="center" justifyContent="center" mt="25px" minH="300px" w="100%">
          <ResponsiveContainer width="100%" height="100%" minHeight={300}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                fontSize={11}
              />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <RechartsTooltip 
                formatter={(value, name) => {
                  if (name === 'usage') return [value, 'Kullanım'];
                  if (name === 'revenue') return [`₺${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Gelir'];
                  return [value, name];
                }}
              />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="usage" 
                stroke="#0088FE" 
                name="Kullanım"
                strokeWidth={3}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="revenue" 
                stroke="#00C49F" 
                name="Gelir (₺)"
                strokeWidth={3}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
    </Card>
  );
}

